import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { analyzeRoute } from './api/routingApi';
import { PlannerState, RouteRiskReport, RouteRiskSettings } from './types';

/** Long enough that dragging a waypoint never fires a scan mid-gesture. */
const DEBOUNCE_MS = 700;

export const DEFAULT_RISK_SETTINGS: RouteRiskSettings = {
  corridorM: 250,
  lookbackHours: 720,
};

export interface UseRouteRisk {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  settings: RouteRiskSettings;
  setSettings: (next: Partial<RouteRiskSettings>) => void;
  report: RouteRiskReport | null;
  loading: boolean;
  error: string | null;
  /**
   * The held report scores a route that is no longer the selected one. Callers
   * that paint the map must treat this as no report at all — segment scores are
   * indexed by position along a specific line, so drawing them on a different
   * line would put real findings in the wrong place.
   */
  stale: boolean;
  capturedAt: string | null;
  refresh: () => void;
}

/**
 * Corridor analysis for the selected route.
 *
 * Opt-in: the scan touches several large tables, so it runs only once the
 * operator asks for it, then re-runs whenever the chosen route changes. The
 * last report is held through hides, edits and failures so an operator reading
 * a hotspot list does not lose it to a stray waypoint drag.
 */
export function useRouteRisk(state: PlannerState): UseRouteRisk {
  const [enabled, setEnabled] = useState(false);
  const [settings, setSettingsState] = useState<RouteRiskSettings>(DEFAULT_RISK_SETTINGS);
  const [report, setReport] = useState<RouteRiskReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [fetchedKey, setFetchedKey] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState<string | null>(null);
  const fetched = useRef<{ key: string; nonce: number } | null>(null);

  const primary = state.routes[state.primaryIndex] || null;

  const geometryKey = useMemo(() => {
    const coords = (primary?.geometry?.coordinates || []) as number[][];
    if (coords.length < 2) return '';
    const first = coords[0];
    const last = coords[coords.length - 1];
    return `${coords.length}:${first[0].toFixed(4)},${first[1].toFixed(4)}`
      + `:${last[0].toFixed(4)},${last[1].toFixed(4)}`;
  }, [primary]);

  const alternatives = useMemo(
    () => state.routes
      .filter((_, index) => index !== state.primaryIndex)
      .map((route) => (route.geometry?.coordinates || []) as number[][]),
    [state.routes, state.primaryIndex],
  );

  const waypoints = useMemo(
    () => state.waypoints.map((w) => [w.longitude, w.latitude]),
    [state.waypoints],
  );

  // Waypoints change identity on every label resolution; key on position only.
  const waypointKey = useMemo(
    () => waypoints.map((w) => w.map((n) => n.toFixed(5)).join(',')).join('|'),
    [waypoints],
  );

  const setSettings = useCallback((next: Partial<RouteRiskSettings>) => {
    setSettingsState((current) => ({ ...current, ...next }));
  }, []);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const waypointsRef = useRef(waypoints);
  waypointsRef.current = waypoints;

  const requestKey = `${geometryKey}|${waypointKey}|${settings.corridorM}|${settings.lookbackHours}`;

  useEffect(() => {
    if (!enabled) return undefined;
    const coordinates = (primary?.geometry?.coordinates || []) as number[][];
    if (coordinates.length < 2) return undefined;
    if (fetched.current?.key === requestKey && fetched.current.nonce === nonce) {
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const next = await analyzeRoute({
          coordinates,
          alternatives,
          waypoints: waypointsRef.current,
          congestion: primary?.congestion || [],
          corridor_m: settings.corridorM,
          segment_m: 500,
          lookback_hours: settings.lookbackHours,
        }, controller.signal);
        if (cancelled) return;
        setReport(next);
        setFetchedKey(requestKey);
        setCapturedAt(new Date().toISOString());
        fetched.current = { key: requestKey, nonce };
      } catch (e: any) {
        if (cancelled || axios.isCancel(e) || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.detail || 'Corridor analysis failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, requestKey, nonce]);

  return {
    enabled,
    setEnabled,
    settings,
    setSettings,
    report,
    loading,
    error,
    stale: !!report && fetchedKey !== requestKey,
    capturedAt,
    refresh,
  };
}
